// src/pages/admin/AdminGradingPage.jsx
import { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert, Stack,
  Table, TableHead, TableRow, TableCell, TableBody,
  Chip, LinearProgress, Button, TextField, Select,
  MenuItem, FormControl, InputLabel, IconButton, Tooltip,
} from '@mui/material';
import GradeIcon      from '@mui/icons-material/Grade';
import RefreshIcon    from '@mui/icons-material/Refresh';
import PlayArrowIcon  from '@mui/icons-material/PlayArrow';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import { runAdminGrading } from '../../api/adminGradingApi';
import { useOrg } from '../../context/OrgContext';

function StatCard({ label, value, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center', flex: 1 }}>
      <Typography fontSize={12} color="text.secondary">{label}</Typography>
      <Typography fontSize={24} fontWeight={700} color={color}>{value}</Typography>
    </Paper>
  );
}

export default function AdminGradingPage() {
  const { t }          = useTranslation('admin');
  const { orgId, orgs } = useOrg();
  const [results,   setResults]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [days,      setDays]      = useState(30);
  const [filter,    setFilter]    = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [running,   setRunning]   = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days, ...(orgFilter !== 'all' ? { org_id: orgFilter } : {}) };
      const r = await api.get('/admin/grading/results', { params });
      setResults(r.data?.items || r.data?.results || r.data || []);
    } catch(e) {
      setError(e?.response?.data?.detail?.message || 'Erreur chargement');
    } finally { setLoading(false); }
  }, [days, orgFilter]);

  useEffect(() => { load(); }, [load]);

  const handleRunGrading = async (reservationId) => {
    setRunning(r => ({ ...r, [reservationId]: true }));
    try { await runAdminGrading(reservationId); await load(); }
    catch(e) { setError(e?.response?.data?.detail?.message || 'Erreur grading'); }
    finally { setRunning(r => ({ ...r, [reservationId]: false })); }
  };

  const filtered = filter === 'all' ? results
    : results.filter(r => filter === 'passed' ? r.passed : !r.passed);

  const total  = results.length;
  const passed = results.filter(r => r.passed).length;
  const avg    = total > 0 ? (results.reduce((s,r) => s + (r.score||0), 0) / total).toFixed(1) : '-';
  const rate   = total > 0 ? Math.round((passed/total)*100) + '%' : '-';

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: 3, py: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <GradeIcon sx={{ fontSize: 28, color: '#0f766e' }} />
          <Typography variant="h5" fontWeight={700}>{t('grading')}</Typography>
        </Stack>
        <IconButton onClick={load} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Stack>

      {/* Stats */}
      <Stack direction="row" spacing={2} mb={3}>
        <StatCard label={t('totalExams', { defaultValue: 'Examens total' })}    value={total}   color="#1e293b" />
        <StatCard label={t('passed', { defaultValue: 'Reussis' })}              value={passed}  color="#16a34a" />
        <StatCard label={t('passRate', { defaultValue: 'Taux reussite' })}      value={rate}    color="#4f46e5" />
        <StatCard label={t('avgScore', { defaultValue: 'Score moyen' })}        value={avg+'%'} color="#b45309" />
      </Stack>

      {/* Filtres */}
      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Periode</InputLabel>
          <Select value={days} onChange={e => setDays(e.target.value)} label="Periode">
            {[7,14,30,60,90].map(d => <MenuItem key={d} value={d}>{d}j</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Resultat</InputLabel>
          <Select value={filter} onChange={e => setFilter(e.target.value)} label="Resultat">
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="passed">Reussis</MenuItem>
            <MenuItem value="failed">Echoues</MenuItem>
          </Select>
        </FormControl>
        {orgs.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('filterByOrg')}</InputLabel>
            <Select value={orgFilter} onChange={e => setOrgFilter(e.target.value)} label={t('filterByOrg')}>
              <MenuItem value="all">{t('allOrgs')}</MenuItem>
              {orgs.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
            </Select>
          </FormControl>
        )}
      </Stack>

      {error  && <Alert severity="error"  sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f8fafc' }}>
            <TableRow>
              <TableCell>Reservation</TableCell>
              <TableCell>Lab</TableCell>
              <TableCell>Utilisateur</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>Resultat</TableCell>
              <TableCell>Declencheur</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Aucun resultat d examen.
                </TableCell>
              </TableRow>
            ) : filtered.map((r, i) => (
              <TableRow key={r.id || i} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>#{r.reservation_id}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>Lab #{r.lab_id || '-'}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{r.username || r.user_id}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LinearProgress variant="determinate" value={Math.min(r.score||0,100)}
                      sx={{ width: 60, height: 5, borderRadius: 2,
                        bgcolor: r.passed ? '#bbf7d0' : '#fecaca',
                        '& .MuiLinearProgress-bar': { bgcolor: r.passed ? '#16a34a' : '#ef4444' } }} />
                    <Typography fontSize={12} fontWeight={600}>{(r.score||0).toFixed(1)}%</Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip label={r.passed ? 'OK' : 'KO'} color={r.passed ? 'success' : 'error'} size="small" />
                </TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{r.trigger}</TableCell>
                <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {r.graded_at ? new Date(r.graded_at).toLocaleString('fr') : '-'}
                </TableCell>
                <TableCell>
                  <Tooltip title="Relancer le grading">
                    <span>
                      <IconButton size="small" onClick={() => handleRunGrading(r.reservation_id)}
                        disabled={!!running[r.reservation_id]}>
                        {running[r.reservation_id]
                          ? <CircularProgress size={14} />
                          : <PlayArrowIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

